import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSpotlight } from '../../hooks/useSpotlight';

const STEPS = [
    {
        n: '01',
        icon: '/assets/app/scan.png',
        image: '/assets/step-1.webp',
        alt: 'Fork camera — framing a meal',
        title: 'Take a photo',
        body: 'Snap your meal or log with voice — no spreadsheets.',
    },
    {
        n: '02',
        icon: '/assets/app/magicwand.png',
        image: '/assets/step-2.webp',
        alt: 'Fork analysing the photo',
        title: 'AI analysis',
        body: 'Identify ingredients, portions, calories, and macros in seconds.',
    },
    {
        n: '03',
        icon: '/assets/app/trends_outline.png',
        image: '/assets/step-3.webp',
        alt: 'Meal detail with calories, macros and meal score',
        title: 'Get insights',
        body: 'See nutrition scores with clear notes you can act on today.',
    },
    {
        n: '04',
        icon: '/assets/app/viewallarrow.png',
        image: '/assets/step-4.webp',
        alt: 'Trends screen with weekly nutrition score',
        title: 'Track progress',
        body: 'Watch patterns over time and get nudges that fit your goals.',
    },
];

interface StepCardProps {
    step: (typeof STEPS)[number];
    isActive: boolean;
    register: (el: HTMLLIElement | null) => void;
}

const StepCard: React.FC<StepCardProps> = ({ step, isActive, register }) => {
    // Active cards use the darker surface; the spotlight paints over whichever is current.
    const base = isActive ? '#292E31' : '#303539';
    const attachSpotlight = useSpotlight<HTMLLIElement>(base);

    return (
        <li
            ref={(el) => {
                attachSpotlight(el);
                register(el);
            }}
            className="relative overflow-hidden rounded-2xl border-[0.8px] p-[clamp(22px,3vw,30px)] transition-[border-color,background] duration-300"
            style={{
                background: base,
                borderColor: isActive ? 'rgba(255,255,255,0.26)' : '#292E31',
            }}
        >
            {/* oversized ghost numeral, bled off the bottom-right */}
            <span
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-[34px] right-2 text-[132px] font-extrabold leading-none tracking-[-0.06em] text-white/[0.045]"
            >
                {step.n}
            </span>

            <div className="mb-3.5 flex items-center gap-3">
                <span className="text-xs font-bold tracking-[0.12em] text-accent-bright">
                    {step.n}
                </span>
                <span
                    className="h-px flex-1 transition-colors duration-300"
                    style={{ background: isActive ? '#0093E7' : '#292E31' }}
                />
                <img
                    src={step.icon}
                    alt=""
                    aria-hidden="true"
                    className="h-[22px] w-[22px] opacity-75"
                />
            </div>
            <h3 className="relative mb-2 text-[clamp(20px,2.2vw,26px)] font-bold tracking-[-0.02em] text-white">
                {step.title}
            </h3>
            <p className="relative text-[16px] leading-[1.6] text-dark-textSecondary">{step.body}</p>
        </li>
    );
};

const HowItWorksSection: React.FC = () => {
    const [active, setActive] = useState(0);
    const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

    const register = useCallback(
        (i: number) => (el: HTMLLIElement | null) => {
            stepRefs.current[i] = el;
        },
        []
    );

    // Driven by scroll position rather than observer margins: pick whichever
    // card sits nearest the viewport's vertical centre, as v1 does.
    useEffect(() => {
        const onScroll = () => {
            const steps = stepRefs.current.filter(Boolean) as HTMLLIElement[];
            if (!steps.length) return;

            const centerY = window.innerHeight * 0.5;
            let best = 0;
            let bestDist = Infinity;

            steps.forEach((s, i) => {
                const r = s.getBoundingClientRect();
                const dist = Math.abs(r.top + r.height / 2 - centerY);
                if (r.bottom > 0 && r.top < window.innerHeight && dist < bestDist) {
                    bestDist = dist;
                    best = i;
                }
            });

            setActive((prev) => (prev === best ? prev : best));
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <section
            id="how"
            className="relative z-[1] scroll-mt-24 border-t border-dark-gunmetal px-5 py-[clamp(64px,9vw,120px)]"
        >
            <div className="mx-auto max-w-[1180px]">
                <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-success">
                    How it works
                </span>
                <h2 className="mt-3.5 max-w-[18ch] text-[clamp(28px,3.8vw,48px)] font-extrabold leading-[1.04] tracking-[-0.035em] text-white">
                    Four simple steps, about ten seconds
                </h2>

                <div className="mt-[clamp(32px,4vw,56px)] grid items-start gap-[clamp(24px,4vw,64px)] lg:grid-cols-2">
                    {/* phone panel — order:2 in v1, so it sits on the right */}
                    <div className="flex min-w-0 flex-col lg:sticky lg:top-[104px] lg:order-2 lg:self-stretch">
                        <div className="mb-3 flex items-center gap-3">
                            <span className="text-[15px] font-extrabold tracking-[-0.01em] text-white">
                                {STEPS[active].n}
                            </span>
                            <span className="text-[13px] text-dark-textSubtle">/ 04</span>
                            <span className="h-0.5 flex-1 overflow-hidden rounded-full bg-dark-gunmetal">
                                <span
                                    className="block h-full rounded-full bg-[linear-gradient(90deg,#0093E7,#02F3A2)] transition-[width] duration-[420ms] ease-[cubic-bezier(.22,1,.36,1)]"
                                    style={{ width: `${((active + 1) / STEPS.length) * 100}%` }}
                                />
                            </span>
                        </div>

                        <div className="relative min-h-[420px] flex-1 overflow-hidden rounded-3xl bg-dark-base shadow-[0_30px_60px_rgba(0,0,0,0.35)]">
                            {STEPS.map((step, i) => (
                                <div
                                    key={step.n}
                                    className="absolute inset-0 transition-[opacity,transform] duration-500 ease-out"
                                    style={{
                                        opacity: i === active ? 1 : 0,
                                        transform: i === active ? 'scale(1)' : 'scale(1.03)',
                                    }}
                                >
                                    <img
                                        src={step.image}
                                        alt={step.alt}
                                        loading="lazy"
                                        className="h-full w-full object-contain"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* steps */}
                    <ol className="grid min-w-0 list-none gap-3 lg:order-1">
                        {STEPS.map((step, i) => (
                            <StepCard
                                key={step.n}
                                step={step}
                                isActive={i === active}
                                register={register(i)}
                            />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default HowItWorksSection;
